import extend from '@ep/bootstrap/extend';
import navigo from '@ep/plugins/navigo';
import Common from '@ep/app/common';
import { getAppConfig, isValidRuntimeConfig } from '@ep/app/common';
import renderPlatformNotFound from '@ep/tools/notFound';
import ajax from '@ep/tools/ajax';
import '@ep/styles/system.scss';
import '../styles/index.scss';

const bootConfig = typeof __BOOT_CONFIG__ !== 'undefined' && __BOOT_CONFIG__
  ? __BOOT_CONFIG__
  : {};
const hasValidBootConfig = isValidRuntimeConfig(bootConfig);
const isProdRuntime = String(process.env.APP_ENV || process.env.NODE_ENV || '').trim().toLowerCase() === 'prod'
  || String(process.env.NODE_ENV || '').trim().toLowerCase() === 'production';
let runtimeConfigPromise = null;

if (typeof window !== 'undefined' && hasValidBootConfig && !getAppConfig()) {
  window.config = bootConfig;
}

const NOT_FOUND_FALLBACK_LABELS = {
  'pt-BR': {
    notFoundPage: 'A pagina que voce esta procurando nao existe.',
    notFoundPage2: 'Voce pode ter digitado incorretamente o endereco ou a pagina pode ter sido removida.',
    notFoundPage3: 'Ir para a pagina principal',
  },
  'en-US': {
    notFoundPage: 'The page you are looking for does not exist.',
    notFoundPage2: 'You may have typed the address incorrectly or the page may have been removed.',
    notFoundPage3: 'Go to the main page',
  },
  'es-ES': {
    notFoundPage: 'La pagina que buscas no existe.',
    notFoundPage2: 'Es posible que hayas escrito mal la direccion o que la pagina haya sido eliminada.',
    notFoundPage3: 'Ir a la pagina principal',
  },
  'ja-JP': {
    notFoundPage: 'お探しのページは存在しません。',
    notFoundPage2: 'URLが間違っているか、ページが削除された可能性があります。',
    notFoundPage3: 'トップページへ戻る',
  },
};

const resolveNotFoundLocale = (locale) => {
  const normalized = typeof locale === 'string' ? locale.trim() : '';
  if (/^[a-z]{2}-[A-Z]{2}$/.test(normalized) && NOT_FOUND_FALLBACK_LABELS[normalized]) {
    return normalized;
  }

  return 'pt-BR';
};

const isTransientProxyFailure = (error) => {
  const message = String(error?.message || '').toLowerCase();
  const status = Number(error?.status || 0);
  return [502, 503, 504].includes(status)
    || message.includes('proxy falhou ao encaminhar')
    || message.includes('bad gateway')
    || message.includes('econnrefused')
    || message.includes('socket hang up')
    || message.includes('failed to fetch');
};

const resolveRoutingConfig = (config = {}) => {
  if (config?.routing && typeof config.routing === 'object') return config.routing;
  if (config?.branding && typeof config.branding === 'object') return config.branding;
  return {};
};

const resolveRuntimeConfig = async () => {
  if (!runtimeConfigPromise) {
    runtimeConfigPromise = ajax.getParam(undefined, { loader: false })
      .then((config) => {
        if (isValidRuntimeConfig(config)) {
          return config;
        }
        if (hasValidBootConfig) {
          return bootConfig;
        }
        return config;
      })
      .catch((error) => {
        runtimeConfigPromise = null;
        if (isProdRuntime) {
          console.error('Falha ao carregar configuracao runtime em producao.', error);
          throw error;
        }
        console.warn('Falha ao carregar configuracao runtime; usando configuracao embarcada.', error);
        return bootConfig;
      });
  }

  const config = await runtimeConfigPromise;
  if (typeof window !== 'undefined' && isValidRuntimeConfig(config)) {
    window.config = config;
  }
  return config;
};

const normalizeRoutingRoutes = (routingConfig) => {
  if (Array.isArray(routingConfig?.routes)) return routingConfig.routes;
  if (routingConfig?.routes && typeof routingConfig.routes === 'object') {
    return Object.entries(routingConfig.routes).map(([key, route]) => ({
      key,
      ...(route && typeof route === 'object' ? route : {}),
    }));
  }
  return [];
};

const collectPublicRouteSlugs = (config) => {
  const routingConfig = resolveRoutingConfig(config);
  const routes = normalizeRoutingRoutes(routingConfig);
  const slugs = new Set();

  routes.forEach((route) => {
    const slug = typeof route?.slug === 'string' ? route.slug.trim().toLowerCase() : '';
    if (slug) slugs.add(slug);

    if (Array.isArray(route?.aliases)) {
      route.aliases.forEach((alias) => {
        const normalized = typeof alias === 'string' ? alias.trim().toLowerCase() : '';
        if (normalized) slugs.add(normalized);
      });
    }
  });

  // rotas internas conhecidas que podem ser chamadas diretamente
  slugs.add('logged');
  slugs.add('unlogged');
  if (!isProdRuntime) {
    slugs.add('tutorial');
  }

  return slugs;
};

/**
 * Inicializa extensões globais de protótipos antes do roteamento.
 */
extend();

/* ===== Roteador ===== */
/**
 * Camada de roteamento da aplicação baseada em Navigo.
 */
class Router extends Common {
  #renderInFlightPath = null;
  #lastRenderedPath = null;
  #transientRetryCount = 0;

  async #resolveCurrentLocation() {
    const config = await resolveRuntimeConfig();
    const languageConfig = config?.language && typeof config.language === 'object' ? config.language : {};
    const defaultLang = resolveNotFoundLocale(languageConfig.default);
    const supportedLangs = Array.isArray(languageConfig.supported) && languageConfig.supported.length > 0
      ? languageConfig.supported
      : [defaultLang];
    const publicSlugs = collectPublicRouteSlugs(config);
    const segments = window.location.pathname
      .split('/')
      .filter(Boolean)
      .map((segment) => {
        try {
          return decodeURIComponent(segment).trim();
        } catch (_) {
          return String(segment || '').trim();
        }
      })
      .filter(Boolean);

    if (segments.length === 0) {
      return { lang: null, page: null };
    }

    if (segments.length === 1) {
      const [first] = segments;

      if (first === '404') {
        return { lang: null, page: '404' };
      }

      if (supportedLangs.includes(first)) {
        return { lang: first, page: null };
      }

      if (publicSlugs.has(first.toLowerCase())) {
        return { lang: null, page: first };
      }

      return { lang: null, page: '404' };
    }

    if (segments.length === 2) {
      const [lang, page] = segments;

      if (!supportedLangs.includes(lang)) {
        return { lang: null, page: '404' };
      }

      if (publicSlugs.has(page.toLowerCase())) {
        return { lang, page };
      }

      return { lang, page: '404' };
    }

    return { lang: null, page: '404' };
  }

  async #renderImmediateNotFound(lang = null) {
    const config = await resolveRuntimeConfig();
    const languageConfig = config?.language && typeof config.language === 'object' ? config.language : {};
    const defaultLang = resolveNotFoundLocale(languageConfig.default);
    const supportedLangs = Array.isArray(languageConfig.supported) && languageConfig.supported.length > 0
      ? languageConfig.supported
      : [defaultLang];
    const resolvedSupportedLangs = supportedLangs.map((item) => resolveNotFoundLocale(item));
    const activeLang = resolvedSupportedLangs.includes(lang) ? lang : defaultLang;
    const labels = NOT_FOUND_FALLBACK_LABELS[activeLang] || NOT_FOUND_FALLBACK_LABELS[defaultLang] || NOT_FOUND_FALLBACK_LABELS['pt-BR'];

    window.config = config;
    window.dic = { lang: activeLang, labels };
    window.language = languageConfig;
    document.documentElement.lang = activeLang;
    document.body.classList.remove('route-switching', 'lgd', 'uld');
    document.body.classList.add('uld');

    await renderPlatformNotFound(window.dic, '/');
  }

  async #renderCurrentLocation() {
    const currentPath = window.location.pathname || '/';
    if (this.#renderInFlightPath === currentPath || this.#lastRenderedPath === currentPath) {
      return;
    }

    this.#renderInFlightPath = currentPath;
    const route = await this.#resolveCurrentLocation();
    try {
      if (route.page === '404') {
        await this.#renderImmediateNotFound(route.lang);
        this.#lastRenderedPath = currentPath;
        return;
      }
      try {
        await this.initializePage(route.lang, route.page);
      } catch (error) {
        if (!isTransientProxyFailure(error)) {
          throw error;
        }

        const retryDelay = Math.min(500 + (this.#transientRetryCount * 250), 3000);
        this.#transientRetryCount += 1;
        console.warn('Falha transitória no boot da aplicação. Tentando novamente.', error);
        window.setTimeout(() => {
          this.clearRenderedPathCache();
          void this.#renderCurrentLocation();
        }, retryDelay);
        return;
      }
      this.#transientRetryCount = 0;
      this.#lastRenderedPath = currentPath;
    } finally {
      this.#renderInFlightPath = null;
    }
  }

  /**
   * Registra rotas e resolve navegação inicial.
   * @public
   * @returns {Promise<void>}
   */
  async render() {
    navigo
      .on({
        '/': async () => {
          await this.#renderCurrentLocation();
        },
        '/404': async () => {
          await this.#renderCurrentLocation();
        },
        '/:page': async () => {
          await this.#renderCurrentLocation();
        },
        '/:lang/:page': async () => {
          await this.#renderCurrentLocation();
        },
      })
      .notFound(() => {
        this.#renderCurrentLocation();
      })
      .resolve();
  }

  clearRenderedPathCache() {
    this.#lastRenderedPath = null;
  }
}

const router = new Router();

/* ===== API publica ===== */
/**
 * Função pública de bootstrap do roteador.
 * @public
 * @returns {Promise<void>}
 */
export const Render = () => router.render();

/**
 * Recarrega o fluxo da rota atual sem depender de mudança de URL.
 * @public
 * @returns {Promise<void>}
 */
export const RefreshCurrentRoute = async () => {
  router.clearRenderedPathCache();
  await navigo.resolve();
};
